/**
 * Recent Prompts — browser of the user's past generation prompts on the
 * Create page. Web port of the native RecentPromptsSheet.
 * Spec: docs/CREATE_RECENTS_WEB_SPEC.md
 *
 * Data source: GET /ai/my-generations?limit={N} (cookie-authed, limit-only —
 * no offset/has_more, so "load more" grows the window and re-fetches).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

const PAGE_SIZE = 40;

interface GenerationRow {
  id: number;
  title?: string | null;
  prompt?: string | null;
  created_at?: string | null;
}

export interface RecentPromptItem {
  id: number;
  prompt: string;
  title: string;
  createdAt: Date | null;
}

/**
 * `prompt` is NOT always a user prompt. File-upload generations store an
 * instruction template + the entire extracted document text. Recover the
 * embedded "User instructions:" line if present; otherwise drop the row.
 */
export function cleanPrompt(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim();
  if (!t) return null;
  const looksLikeFileUpload =
    t.startsWith('Transform this source document') || t.includes('Source document content:');
  if (looksLikeFileUpload) {
    const marker = 'User instructions:';
    const i = t.indexOf(marker);
    if (i !== -1) {
      const after = t.slice(i + marker.length);
      const instr = after.split('\n\n')[0].trim();
      return instr || null;
    }
    return null;
  }
  return t;
}

/** Server emits SQLite datetime, optionally " UTC"-suffixed. Treat as UTC. */
function parseCreatedAt(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const m = raw.trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]));
  return isNaN(d.getTime()) ? null : d;
}

function relativeTime(date: Date | null): string {
  if (!date) return '';
  const secs = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

/** Clean every row, drop empties, dedupe case-insensitively keeping the newest
 *  (rows arrive newest-first, so first occurrence wins). */
function toCleanItems(rows: GenerationRow[]): RecentPromptItem[] {
  const seen = new Set<string>();
  const out: RecentPromptItem[] = [];
  for (const row of rows) {
    const prompt = cleanPrompt(row.prompt);
    if (!prompt) continue;
    const key = prompt.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: row.id,
      prompt,
      title: (row.title || '').trim(),
      createdAt: parseCreatedAt(row.created_at),
    });
  }
  return out;
}

/**
 * The Recent prompts sheet. Fetches on open, endless-scrolls by growing the
 * limit window (the endpoint has no real paging), and hosts the detail modal.
 */
export function RecentPromptsSheet({
  open,
  onClose,
  onUsePrompt,
}: {
  open: boolean;
  onClose: () => void;
  onUsePrompt: (prompt: string) => void;
}) {
  const [items, setItems] = useState<RecentPromptItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [endReached, setEndReached] = useState(false);
  const [detail, setDetail] = useState<RecentPromptItem | null>(null);
  const [copied, setCopied] = useState(false);
  // Bumps after every completed fetch so the observer effect re-runs even when
  // a grown window added zero visible prompts (heavy file-upload filtering
  // leaves items/endReached unchanged, which would otherwise stall load-more).
  const [fetchSeq, setFetchSeq] = useState(0);

  // The current window size. "There may be more" is inferred from the RAW row
  // count filling the requested window — cleaning can drop many rows, so the
  // visible count says nothing about the end.
  const limitRef = useRef(PAGE_SIZE);
  const fetchingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const copiedTimerRef = useRef<number | null>(null);

  const fetchWindow = useCallback(async (limit: number) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetch(`/ai/my-generations?limit=${limit}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rows: GenerationRow[] = Array.isArray(data?.generations) ? data.generations : [];
      limitRef.current = limit;
      setItems(toCleanItems(rows));
      // Fewer raw rows than requested = we've seen everything.
      setEndReached(rows.length < limit);
    } catch (e) {
      console.error('[RecentPrompts] fetch failed:', e);
      setLoadError(true);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setFetchSeq((n) => n + 1);
    }
  }, []);

  // Re-fetch on every open so the list reflects generations since last look.
  useEffect(() => {
    if (!open) return;
    limitRef.current = PAGE_SIZE;
    setEndReached(false);
    setDetail(null);
    fetchWindow(PAGE_SIZE);
  }, [open, fetchWindow]);

  // Endless scroll: while the sentinel is visible and more may exist, keep
  // growing the window — even if a fetch added few visible prompts (heavy
  // file-upload filtering), the observer stays intersected and re-advances.
  useEffect(() => {
    if (!open || endReached) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !fetchingRef.current) {
        fetchWindow(limitRef.current + PAGE_SIZE);
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [open, endReached, fetchSeq, fetchWindow]);

  // Escape closes detail first, then the sheet.
  const detailRef = useRef<RecentPromptItem | null>(null);
  detailRef.current = detail;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (detailRef.current) setDetail(null);
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    setCopied(false);
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
  }, [detail]);

  useEffect(() => () => {
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
  }, []);

  if (!open) return null;

  const handleCopy = async () => {
    if (!detail) return;
    try {
      await navigator.clipboard.writeText(detail.prompt);
    } catch {
      // Clipboard API can be unavailable (http, permissions) — fall back.
      const ta = document.createElement('textarea');
      ta.value = detail.prompt;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    }
    setCopied(true);
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="ai-recents-overlay" onClick={onClose} role="presentation">
      <div
        className="ai-recents-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Recent prompts"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ai-recents-header">
          <h2>Recent prompts</h2>
          <button type="button" className="ai-recents-close" aria-label="Close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="ai-recents-list">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className="ai-recents-row"
              onClick={() => setDetail(item)}
            >
              <span className="ai-recents-row-prompt">{item.prompt}</span>
              <span className="ai-recents-row-meta">
                {item.title && <span className="ai-recents-row-title">{item.title}</span>}
                {item.title && item.createdAt && <span aria-hidden="true">·</span>}
                {item.createdAt && <span>{relativeTime(item.createdAt)}</span>}
              </span>
            </button>
          ))}

          {!loading && !loadError && items.length === 0 && endReached && (
            <div className="ai-recents-empty">
              <div className="ai-recents-empty-title">No recent prompts yet</div>
              <div className="ai-recents-empty-hint">
                Prompts you use to generate flows show up here.
              </div>
            </div>
          )}

          {loadError && (
            <div className="ai-recents-empty">
              <div className="ai-recents-empty-title">Couldn't load your prompts</div>
              <button
                type="button"
                className="ai-recents-retry"
                onClick={() => fetchWindow(limitRef.current)}
              >
                Try again
              </button>
            </div>
          )}

          {!endReached && !loadError && <div ref={sentinelRef} className="ai-recents-sentinel" />}

          {loading && <div className="ai-recents-loading">Loading…</div>}

          {endReached && items.length > 0 && (
            <div className="ai-recents-end">
              {items.length} prompt{items.length === 1 ? '' : 's'}
            </div>
          )}
        </div>
      </div>

      {detail && (
        <div
          className="ai-recents-detail-overlay"
          role="presentation"
          onClick={(e) => { e.stopPropagation(); setDetail(null); }}
        >
          <div
            className="ai-recents-detail"
            role="dialog"
            aria-modal="true"
            aria-label="Prompt detail"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ai-recents-header">
              <h2>Prompt</h2>
              <button
                type="button"
                className="ai-recents-close"
                aria-label="Close"
                onClick={() => setDetail(null)}
              >
                &times;
              </button>
            </div>
            <div className="ai-recents-detail-body">
              <p className="ai-recents-detail-text">{detail.prompt}</p>
              <div className="ai-recents-detail-meta">
                {detail.title && <span>{detail.title}</span>}
                {detail.title && detail.createdAt && <span aria-hidden="true">·</span>}
                {detail.createdAt && <span>{relativeTime(detail.createdAt)}</span>}
              </div>
            </div>
            <div className="ai-recents-detail-actions">
              <button
                type="button"
                className={`ai-recents-copy-btn ${copied ? 'copied' : ''}`}
                onClick={handleCopy}
              >
                {copied ? 'Copied ✓' : 'Copy'}
              </button>
              <button
                type="button"
                className="ai-recents-use-btn"
                onClick={() => onUsePrompt(detail.prompt)}
              >
                Use this prompt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
